import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import { filterOptionsWithSynonyms, getDatePickerDisabledRange, getEnvironment, useTranslation } from "utils";

import { CurrencyCode } from "consts";

import {
  FertilizerApplication,
  FertilizersResponse,
  PracticeFertilizerApplication,
  PracticeFertilizerMineral,
  PracticeFertilizerOrganic,
  WithInjectedName,
  ZERO_UUID,
} from "models";

import { Flex, Form, IconButton, InputNumber, Option, RadioChangeEvent, RangePicker, Select } from "components";

import { CustomKlimRadioGroupWithPayouts } from "pages/app/all-field-view/components/forms/fertilizers/CustomKlimRadioGroupWithPayouts";
import { CALENDAR_YEAR_END, CALENDAR_YEAR_START } from "pages/app/all-field-view/constants/cropForm";

type OrganicOrMineralFertilizerFormProps = {
  chosenFertilizerType: "organic" | "mineral";
  fertilizers: FertilizersResponse;
  existingFertilizerApplication: FertilizerApplication;
  index: number;
  onRemove: () => void;
  onUpdate?: (index: number, values: Partial<FertilizerApplication>) => void;
  recommendedSeedingDate?: string;
  currency?: CurrencyCode;
};

const createOption = ({
  index,
  option,
  groupName,
}: {
  index: number;
  option: WithInjectedName<PracticeFertilizerApplication | PracticeFertilizerMineral | PracticeFertilizerOrganic>;
  groupName: string;
}) => (
  <Option
    data-testid={`add-edit-fertilizer-form-selection-${groupName}-option-${index}`}
    key={`${groupName}-${option.id}`}
    label={[option.name]}
    value={option.id}
  >
    {option.name}
  </Option>
);

export const OrganicOrMineralFertilizerForm = ({
  chosenFertilizerType,
  fertilizers,
  existingFertilizerApplication,
  index,
  onRemove,
  recommendedSeedingDate,
  currency,
}: OrganicOrMineralFertilizerFormProps) => {
  const { t } = useTranslation(["modules", "allFieldView"]);
  const [applicationTypeId, setApplicationTypeId] = useState<string | null>(() => existingFertilizerApplication.applicationType ?? null);
  const [productName, setProductName] = useState<string | null>(() => existingFertilizerApplication.assignedPracticeId ?? null);
  const [isSelfProduced, setIsSelfProduced] = useState<boolean | null>(existingFertilizerApplication.selfProduced ?? null);
  const [amount, setAmount] = useState<number | null>(() => existingFertilizerApplication.amount ?? null);
  const [nValue, setNValue] = useState<number | null>(() => existingFertilizerApplication.nValue ?? null);
  const [pickerKey, setPickerKey] = useState(`${chosenFertilizerType}-${index}`);

  const isAdmin = getEnvironment().admin;

  const handleSelfProducedChange = useCallback((e: RadioChangeEvent) => {
    setIsSelfProduced(!!e.target.value);
  }, []);

  useEffect(() => {
    // Force the RangePicker to re-evaluate defaultValue by remounting it whenever recommendedSeedingDate changes.
    // Since defaultValue is only used for the initial mount and does not update dynamically.
    setPickerKey((prevState) => prevState + 1);
  }, [recommendedSeedingDate]);

  return (
    <>
      <div className="margin-bottom-150">
        <Flex justify="space-between" align="center" className="margin-bottom-100">
          <h5 className="fertilizers-title"> {t("allFieldView:fertilizerForm.application", { number: index + 1 })}</h5>
          <IconButton
            type="tertiary"
            size="small"
            iconName="tabler/trash"
            dataTestId="fertilizers-body-container-delete-icon"
            onClick={onRemove}
            disabled={existingFertilizerApplication.isConfirmed && !isAdmin}
          />
        </Flex>

        <div className="fertilizers-input-container">
          <Form.Item hidden name={[`${chosenFertilizerType}-${index}`, "id"]} initialValue={existingFertilizerApplication.id} />
          <Form.Item hidden name={[`${chosenFertilizerType}-${index}`, "type"]} initialValue={existingFertilizerApplication.type} />
          {/* Product Name */}
          {/* @ts-expect-error need to convert to tsx */}
          <Select
            label={t("modules:bauEntryDrawer.fertilizer.productName")}
            name={[`${chosenFertilizerType}-${index}`, "assignedPracticeId"]}
            initialValue={existingFertilizerApplication.assignedPracticeId}
            rules={[{ message: "", required: true }]}
            showSearch
            optionFilterProp="children"
            filterOption={filterOptionsWithSynonyms}
            dataTestId={`fertilizer-select-${index}-${chosenFertilizerType}`}
            value={productName}
            onChange={(value) => setProductName(value)}
            disabled={existingFertilizerApplication.isConfirmed && !isAdmin}
          >
            {chosenFertilizerType &&
              fertilizers[chosenFertilizerType]
                .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
                // If the initial value is of a disabled element we want to render it,
                // all other disabled ones not
                .filter(
                  (fertilizer) => fertilizer.id !== ZERO_UUID && (fertilizer.enabled || fertilizer.id === existingFertilizerApplication.assignedPracticeId)
                )
                .map((fertilizer, index: number) => createOption({ index: index, option: fertilizer, groupName: "all" }))}
          </Select>

          {/* Was it produced on this farm? */}
          {chosenFertilizerType === "organic" && (
            <Flex className="row-space-between" justify="space-between" align="center" gap="1rem">
              <span className="paragraph-m">{t("modules:bauEntryDrawer.fertilizer.selfProduced.title")}</span>

              <Form.Item
                name={[`${chosenFertilizerType}-${index}`, "selfProduced"]}
                initialValue={existingFertilizerApplication.selfProduced}
                rules={[{ message: "", required: true }]}
                required
              >
                <CustomKlimRadioGroupWithPayouts
                  value={isSelfProduced}
                  onChange={handleSelfProducedChange}
                  dataTestId="add-edit-fertilizer-form-self-produced-radio-group"
                  className="fertilizers-radio"
                  currency={currency}
                  disabled={existingFertilizerApplication.isConfirmed && !isAdmin}
                />
              </Form.Item>
            </Flex>
          )}

          <RangePicker
            key={pickerKey}
            dataTestId={`add-edit-fertilizer-form-date-range-${existingFertilizerApplication.assignedPracticeId}-picker`}
            className="harvest-form-datepicker"
            format={["DD.MM.YYYY"]}
            initialValue={[
              existingFertilizerApplication.startDate ? moment(existingFertilizerApplication.startDate) : null,
              existingFertilizerApplication.endDate ? moment(existingFertilizerApplication.endDate) : null,
            ]}
            defaultPickerValue={recommendedSeedingDate ? [moment(recommendedSeedingDate), moment(recommendedSeedingDate)] : undefined}
            label={t("allFieldView:fertilizerForm.dateRange")}
            name={[`${chosenFertilizerType}-${index}`, "dateRange"]}
            rules={[
              { message: "", required: true },
              {
                validator: (_, value: moment.Moment[] | null) => {
                  if (!value?.[1]) {
                    return Promise.reject(new Error(t("allFieldView:fertilizerForm.errorMessages.dateRangeRequired")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            // Once submitted, the date can still be changed within the respective calendar year.
            disabledDate={(current) =>
              !isAdmin &&
              existingFertilizerApplication.isConfirmed &&
              !!existingFertilizerApplication.year &&
              getDatePickerDisabledRange({
                current,
                startDate: `${CALENDAR_YEAR_START}${existingFertilizerApplication.year}`,
                endDate: `${CALENDAR_YEAR_END}${existingFertilizerApplication.year}`,
              })
            }
          />

          {/* Amount */}
          <InputNumber
            dataTestId={`fertilizer-amount-${index}-${chosenFertilizerType}`}
            decimalSeparator=","
            initialValue={existingFertilizerApplication.amount}
            label={t("allFieldView:fertilizerForm.amount")}
            name={[`${chosenFertilizerType}-${index}`, "amount"] as unknown as string}
            min={0.01}
            rules={[{ message: "", required: true }]}
            step={0.01}
            suffix={t(`allFieldView:fertilizerForm.${chosenFertilizerType}AmountSuffix`)}
            value={amount}
            onChange={(value) => setAmount(value)}
            disabled={existingFertilizerApplication.isConfirmed && !isAdmin}
          />

          {/* N-value */}
          {chosenFertilizerType === "organic" && (
            <InputNumber
              decimalSeparator=","
              initialValue={existingFertilizerApplication.nValue}
              label={t("allFieldView:fertilizerForm.nValue")}
              min={0.01}
              name={[`${chosenFertilizerType}-${index}`, "nValue"] as unknown as string} // casting this here cos of the shape of InputNumber, while ant accepts array & string
              rules={[{ message: "", required: true }]}
              step={0.01}
              suffix={t("allFieldView:fertilizerForm.nValueSuffix")}
              value={nValue}
              onChange={(value) => setNValue(value)}
              disabled={existingFertilizerApplication.isConfirmed && !isAdmin}
            />
          )}

          {/* Application Type */}
          {chosenFertilizerType === "organic" && (
            <>
              {/* @ts-expect-error need to convert to tsx */}
              <Select
                label={t("modules:bauEntryDrawer.fertilizer.applicationType")}
                name={[`${chosenFertilizerType}-${index}`, "applicationType"]}
                initialValue={applicationTypeId}
                rules={[{ message: "", required: true }]}
                showSearch
                optionFilterProp="children"
                filterOption={filterOptionsWithSynonyms}
                value={applicationTypeId}
                onChange={(value) => setApplicationTypeId(value)}
                disabled={existingFertilizerApplication.isConfirmed && !isAdmin}
              >
                {fertilizers.applications
                  // If the initial value is of a disabled element we want to render it,
                  // all other disabled ones not
                  .filter(
                    (application) => application.id !== ZERO_UUID && (application.enabled || application.id === existingFertilizerApplication.applicationType)
                  )
                  .map((application, index: number) => createOption({ index: index, option: application, groupName: "applications" }))}
              </Select>
            </>
          )}
        </div>
      </div>
    </>
  );
};
